import axios from 'axios'
import { store } from '../store/store.js'
import { logout } from '../store/slices/authSlice.js'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://facedevapi.intellisiatechnologies.com';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('device_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add organization ID for org-specific requests only if not already provided
    const orgId = localStorage.getItem('org_id')
    if (orgId && config.url.includes('/org/') && !config.headers['X-Org-ID']) {
      config.headers['X-Org-ID'] = orgId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Handle success messages from backend
    // Skip toast if the request explicitly sets skipSuccessToast
    if (response.data?.message && response.config.method !== 'get' && !response.config.skipSuccessToast) {
      toast.success(response.data.message, {
        duration: 3000,
        position: 'top-right',
      })
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      const loginEndpoints = [
        '/auth/superadmin/login',
        '/auth/employee/login',
        '/auth/org/login',
        '/devices/auth',
        '/employee/attendance/mark',  // QR / unauthenticated mark — don't log out on 401
      ]

      // Check if the request URL matches any login endpoint
      const isLoginRequest = loginEndpoints.some(endpoint =>
        originalRequest.url.includes(endpoint)
      )

      // If it's a login request, don't redirect/logout, let the component handle the error
      if (isLoginRequest) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      // Logout user
      store.dispatch(logout())
      const wasDevice = !!localStorage.getItem('device_token')
      localStorage.clear()

      toast.error('Session expired. Please login again.', {
        duration: 4000,
        position: 'top-center',
      })

      // Redirect to login based on user type
      const userType = localStorage.getItem('user_type')
      if (wasDevice) {
        window.location.href = '/device/login'
      } else if (userType === 'superadmin') {
        window.location.href = '/super-admin/login'
      } else {
        window.location.href = '/org/login'
      }
    }

    // Handle other errors
    const errorData = error.response?.data
    if (errorData?.message || errorData?.detail) {
      // Skip toast if the request explicitly sets skipErrorToast
      if (originalRequest?.skipErrorToast) {
        return Promise.reject(error)
      }

      let errorMessage = errorData.message || errorData.detail

      // Handle Pydantic validation errors (array of objects or single object)
      if (typeof errorMessage === 'object') {
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage.map(err => err.msg || JSON.stringify(err)).join('\n')
        } else {
          errorMessage = errorMessage.msg || JSON.stringify(errorMessage)
        }
      }

      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      })
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your connection.', {
        duration: 4000,
        position: 'top-right',
      })
    }

    return Promise.reject(error)
  }
)

// File upload interceptor
export const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
})

uploadApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const orgId = localStorage.getItem('org_id')
    if (orgId && !config.headers['X-Org-ID']) {
      config.headers['X-Org-ID'] = orgId
    }
    return config
  },
  (error) => Promise.reject(error)
)

export default api